import supabase from './supabaseClient';
import { getActivityCategory } from '../models/activityTypes';

/**
 * Service pour enregistrer les activités des utilisateurs
 * Fournit une traçabilité complète de toutes les actions dans l'application
 */

/**
 * Enregistre une activité dans la base de données
 * @param {string} activityType - Type d'activité (ex: ACTIVITY_TYPES.SUBSCRIBER_CREATED)
 * @param {string} userId - ID de l'utilisateur qui a effectué l'action
 * @param {string} entityType - Type d'entité affectée (subscriber, meter, payment, etc.)
 * @param {string} entityId - ID de l'entité affectée
 * @param {object} metadata - Données supplémentaires sur l'action (avant, après, raison, etc.)
 * @param {string} description - Description lisible de l'action
 * @returns {Promise<object>} Résultat de l'opération
 */
export const logActivity = async (
  activityType,
  userId,
  entityType,
  entityId,
  metadata = {},
  description = ''
) => {
  try {
    const category = getActivityCategory(activityType);
    
    const { data, error } = await supabase
      .from('activity_logs')
      .insert([
        {
          activity_type: activityType,
          user_id: userId,
          entity_type: entityType,
          entity_id: entityId,
          category,
          metadata: metadata || {},
          description: description || '',
          ip_address: await getClientIpAddress(),
          user_agent: navigator.userAgent,
          timestamp: new Date().toISOString()
        }
      ])
      .select()
      .single();

    if (error) {
      console.error('Erreur lors de l\'enregistrement de l\'activité:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Erreur lors de l\'enregistrement de l\'activité:', error);
    return { success: false, error };
  }
};

/**
 * Récupère l'historique des activités avec filtres optionnels
 * @param {object} filters - Filtres optionnels
 * @param {string} filters.userId - Filtrer par utilisateur
 * @param {string} filters.entityType - Filtrer par type d'entité
 * @param {string} filters.entityId - Filtrer par ID d'entité
 * @param {string} filters.category - Filtrer par catégorie
 * @param {string} filters.activityType - Filtrer par type d'activité
 * @param {string} filters.startDate - Date de début (ISO format)
 * @param {string} filters.endDate - Date de fin (ISO format)
 * @param {number} filters.limit - Nombre de résultats (défaut: 100)
 * @param {number} filters.offset - Décalage pour la pagination
 * @returns {Promise<object>}
 */
export const getActivityLogs = async (filters = {}) => {
  try {
    const {
      userId,
      entityType,
      entityId,
      category,
      activityType,
      startDate,
      endDate,
      limit = 100,
      offset = 0
    } = filters;

    let query = supabase
      .from('activity_logs')
      .select('*', { count: 'exact' });

    // Appliquer les filtres
    if (userId) {
      query = query.eq('user_id', userId);
    }
    if (entityType) {
      query = query.eq('entity_type', entityType);
    }
    if (entityId) {
      query = query.eq('entity_id', entityId);
    }
    if (category) {
      query = query.eq('category', category);
    }
    if (activityType) {
      query = query.eq('activity_type', activityType);
    }
    if (startDate) {
      query = query.gte('timestamp', startDate);
    }
    if (endDate) {
      query = query.lte('timestamp', endDate);
    }

    // Pagination et tri
    const { data, error, count } = await query
      .order('timestamp', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      console.error('Erreur lors de la récupération des logs:', error);
      return { success: false, error };
    }

    return {
      success: true,
      data: data || [],
      count: count || 0,
      hasMore: count > offset + limit
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des logs:', error);
    return { success: false, error };
  }
};

/**
 * Récupère l'historique des activités pour une entité spécifique
 * @param {string} entityType - Type d'entité
 * @param {string} entityId - ID de l'entité
 * @param {number} limit - Nombre de résultats
 * @returns {Promise<object>}
 */
export const getEntityActivityHistory = async (entityType, entityId, limit = 50) => {
  return getActivityLogs({
    entityType,
    entityId,
    limit
  });
};

/**
 * Récupère l'historique des activités d'un utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {number} limit - Nombre de résultats
 * @returns {Promise<object>}
 */
export const getUserActivityHistory = async (userId, limit = 50) => {
  return getActivityLogs({
    userId,
    limit
  });
};

/**
 * Récupère les statistiques d'activité
 * @param {object} filters - Filtres optionnels
 * @returns {Promise<object>}
 */
export const getActivityStatistics = async (filters = {}) => {
  try {
    const { startDate, endDate } = filters;

    let query = supabase
      .from('activity_logs')
      .select('activity_type, category, user_id, count(*)', { count: 'exact' });

    if (startDate) {
      query = query.gte('timestamp', startDate);
    }
    if (endDate) {
      query = query.lte('timestamp', endDate);
    }

    const { data, error } = await query.groupBy('activity_type, category, user_id');

    if (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      return { success: false, error };
    }

    return {
      success: true,
      data: data || []
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques:', error);
    return { success: false, error };
  }
};

/**
 * Supprime les logs d'activité anciens (plus de X jours)
 * Utile pour la gestion des archivages
 * @param {number} daysOld - Nombre de jours
 * @returns {Promise<object>}
 */
export const deleteOldActivityLogs = async (daysOld = 90) => {
  try {
    const date = new Date();
    date.setDate(date.getDate() - daysOld);
    const cutoffDate = date.toISOString();

    const { data, error } = await supabase
      .from('activity_logs')
      .delete()
      .lt('timestamp', cutoffDate);

    if (error) {
      console.error('Erreur lors de la suppression des anciens logs:', error);
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    console.error('Erreur lors de la suppression des anciens logs:', error);
    return { success: false, error };
  }
};

/**
 * Supprime les logs d'activité selon une plage de dates
 * @param {string} startDate - Date de début (ISO format)
 * @param {string} endDate - Date de fin (ISO format)
 * @returns {Promise<object>} Résultat de l'opération avec le nombre d'enregistrements supprimés
 */
export const deleteActivityLogsByDateRange = async (startDate, endDate) => {
  try {
    // D'abord, compter les enregistrements qui vont être supprimés
    let countQuery = supabase
      .from('activity_logs')
      .select('*', { count: 'exact', head: true });

    if (startDate) {
      countQuery = countQuery.gte('timestamp', startDate);
    }
    if (endDate) {
      countQuery = countQuery.lte('timestamp', endDate);
    }

    const { count: deletedCount, error: countError } = await countQuery;

    if (countError) {
      console.error('Erreur lors du comptage des logs:', countError);
      return { success: false, error: countError, deletedCount: 0 };
    }

    // Supprimer les enregistrements
    let deleteQuery = supabase
      .from('activity_logs')
      .delete();

    if (startDate) {
      deleteQuery = deleteQuery.gte('timestamp', startDate);
    }
    if (endDate) {
      deleteQuery = deleteQuery.lte('timestamp', endDate);
    }

    const { error: deleteError } = await deleteQuery;

    if (deleteError) {
      console.error('Erreur lors de la suppression des logs:', deleteError);
      return { success: false, error: deleteError, deletedCount: 0 };
    }

    return { success: true, deletedCount: deletedCount || 0 };
  } catch (error) {
    console.error('Erreur lors de la suppression des logs par plage:', error);
    return { success: false, error, deletedCount: 0 };
  }
};

/**
 * Enregistre une connexion utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {string} email - Email de l'utilisateur
 * @returns {Promise<object>}
 */
export const logUserLogin = async (userId, email) => {
  const { ACTIVITY_TYPES } = await import('../models/activityTypes');
  return logActivity(
    ACTIVITY_TYPES.USER_LOGIN,
    userId,
    'user',
    userId,
    { email },
    `Connexion - ${email}`
  );
};

/**
 * Enregistre une déconnexion utilisateur
 * @param {string} userId - ID de l'utilisateur
 * @param {string} email - Email de l'utilisateur
 * @returns {Promise<object>}
 */
export const logUserLogout = async (userId, email) => {
  const { ACTIVITY_TYPES } = await import('../models/activityTypes');
  return logActivity(
    ACTIVITY_TYPES.USER_LOGOUT,
    userId,
    'user',
    userId,
    { email },
    `Déconnexion - ${email}`
  );
};

/**
 * Obtient l'adresse IP du client
 * Note: Cette fonction est une approximation car elle utilise navigator API
 * @returns {Promise<string>}
 */
async function getClientIpAddress() {
  try {
    // Tentative d'utiliser une API publique pour obtenir l'IP
    // Dans un environnement de production, vous devriez utiliser les en-têtes du serveur
    const response = await fetch('https://api.ipify.org?format=json', { 
      mode: 'cors',
      cache: 'no-cache'
    });
    const data = await response.json();
    return data.ip || 'unknown';
  } catch (error) {
    return 'unknown';
  }
}

/**
 * Exporte les logs d'activité au format JSON
 * @param {object} filters - Filtres optionnels
 * @returns {Promise<string>} JSON string
 */
export const exportActivityLogsAsJson = async (filters = {}) => {
  try {
    const result = await getActivityLogs({ ...filters, limit: 10000 });
    
    if (!result.success) {
      throw result.error;
    }

    return JSON.stringify(result.data, null, 2);
  } catch (error) {
    console.error('Erreur lors de l\'export des logs:', error);
    throw error;
  }
};

/**
 * Exporte les logs d'activité au format CSV
 * @param {object} filters - Filtres optionnels
 * @returns {Promise<string>} CSV string
 */
export const exportActivityLogsAsCSV = async (filters = {}) => {
  try {
    const result = await getActivityLogs({ ...filters, limit: 10000 });
    
    if (!result.success) {
      throw result.error;
    }

    const headers = ['Date', 'Utilisateur', 'Type d\'activité', 'Entité', 'ID Entité', 'Description', 'IP', 'Navigateur'];
    const rows = result.data.map(log => [
      new Date(log.timestamp).toLocaleString('fr-FR'),
      log.user_id,
      log.activity_type,
      log.entity_type,
      log.entity_id,
      log.description,
      log.ip_address,
      log.user_agent
    ]);

    const csv = [
      headers.join(';'),
      ...rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    return csv;
  } catch (error) {
    console.error('Erreur lors de l\'export CSV:', error);
    throw error;
  }
};
